import * as React from 'react';
import { compose } from 'redux';
import * as Loadable from 'react-loadable';
import ConnectionNotification from '@om-fe/core/components/notification/connection-notification';
import BreakpointWrapper from '@om-fe/core/components/breakpoint-wrapper/breakpoint-wrapper';
import TemplateContent, { BaseTemplatePublicProps } from '@om-fe/core/core/routing/templates/template-content';
import { bemContent } from '@om-fe/core/core/routing/templates/template.utils';
import { TemplateProps } from '@om-fe/core/core/routing/templates/template.models';
import { FavoriteState, FavoriteTournaments } from '@om-fe/core/components/favorites/favorite.utils';
import FavoriteTeams from '@om-fe/core/components/favorite-teams/favorite-teams';
import { Breakpoints } from '@om-fe/core/core/store/breakpoints';
import { EMRoutingContextInterface, RouteModel, RouteNames } from '@om-fe/core/core/routing/utils';
import withRouting from '@om-fe/core/core/routing/with-routing';
import getConfig, { LiveEventConfig, OMFE2ConfigModel, MarketsHeaderOptions, CasinoFooterVersion } from '@om-fe/core/utils/config';
import { RightSideBarSkeleton } from '../components/right-side-bar-skeleton';
import { SCROLL_CONTAINER_TOP_CLASS } from '@om-fe/core/utils/dom';
import LiveEventSidebar from '@om-fe/core/components/live-event/sidebar/sports-sidebar/live-event-sidebar';
import { DEFAULT_LIVE_SIDEBAR_ODDS_EXPANDED, DEFAULT_LIVE_SIDEBAR_GROUPS_EXPANDED, DEFAULT_LIVE_SIDEBAR_EVENT_NUMBER } from '../components/match-list/utils/match-list.constants';
import { DisciplineLiveStatus, DisciplineVirtualStatus } from '@om-fe/models';
import { LIVE_SIDEBAR_NAV_MAIN_MARKETS } from '@om-fe/core/components/live-event/sidebar/match-list/live-event-sidebar-match-list.constants';
import { CasinoFooter } from '@om-fe/core/components/external-widgets/casino-footer';
import { BrowserOnly } from '@om-fe/widgets/dist/components/ssr/browser-only';
import BetslipBubble from '../components/betslip-bubble/betslip-bubble';
import Header from '../pages/parts/header';
import { SearchPageComponent } from '@om-fe/core/pages/theme1/search-page';
import { FooterTemplate } from '@om-fe/core/components/external-widgets/footer-template';
import { isBrowser } from '@om-fe/utils';
import { BetslipWrapperMobile } from '../components/betslip-wrapper/betslip-wrapper-mobile';
import RightSideBar from '../components/right-side-bar';
import { SimulateWidget } from '@om-fe/core/components/simulate/simulate-widget';

declare global {
    namespace JSX {
        interface IntrinsicElements {
            'casino-footer': any;
        }
    }
}

const config: OMFE2ConfigModel & { customConfig: { siteUrl: string, casinoFooterUrl?: string } } = getConfig();

class LiveTemplateComponent extends React.PureComponent<TemplateProps & EMRoutingContextInterface> {

    render() {
        const { match: { url }, getRouteByUrl } = this.props;
        const routeModel: RouteModel | undefined = getRouteByUrl(url);
        const routeName: RouteNames | undefined = routeModel && routeModel.name;
        const configLiveEvent: LiveEventConfig | undefined = config.matchListConfig && config.matchListConfig.liveEvent;
        const numberOfOddsExpanded = (configLiveEvent?.numberOfOddsExpanded) ?? DEFAULT_LIVE_SIDEBAR_ODDS_EXPANDED;
        const numberOfGroupsExpanded = (configLiveEvent && configLiveEvent.numberOfGroupsExpanded) ?? DEFAULT_LIVE_SIDEBAR_GROUPS_EXPANDED;
        const eventsNumber = (configLiveEvent && configLiveEvent.eventsNumber) ?? DEFAULT_LIVE_SIDEBAR_EVENT_NUMBER;
        const marketsHeaderOptions: MarketsHeaderOptions | undefined = configLiveEvent && configLiveEvent.marketsHeaderOptions;

        return (
            <>
                <FavoriteState />
                <FavoriteTournaments />
                <FavoriteTeams />
                <SimulateWidget />
                {(routeName !== RouteNames.liveSports && routeName !== RouteNames.newVirtualSports) && (
                    <BreakpointWrapper breakpoints={[Breakpoints.Large, Breakpoints.XL]}>
                        <LiveEventSidebar
                            instanceId={'Live-event-left-sidebar'}
                            limitNumberOfEvents={eventsNumber}
                            liveStatus={DisciplineLiveStatus.LIVE}
                            numberOfGroupsExpanded={numberOfGroupsExpanded}
                            numberOfOddsExpanded={numberOfOddsExpanded}
                            routeName={RouteNames.liveEvent}
                            virtualStatus={DisciplineVirtualStatus.BOTH}
                            mainMarketsLimit={LIVE_SIDEBAR_NAV_MAIN_MARKETS}
                            showSportName={true}
                            marketsHeaderOptions={marketsHeaderOptions}
                        />
                    </BreakpointWrapper>
                )}
                <section className={bemContent({ extra: SCROLL_CONTAINER_TOP_CLASS })}>
                    {config.enabledTopMenu && (
                        <BreakpointWrapper breakpoints={[Breakpoints.Small, Breakpoints.Medium]}>
                            <Header
                                hasSportNav={false}
                                defaultSearchMap={SearchPageComponent.defaultSearchMap}
                                searchRouteName={RouteNames.search}
                            />
                        </BreakpointWrapper>
                    )}
                    <div className={bemContent('MainArea')}>
                        <ConnectionNotification />
                        <TemplateContent {...this.props} />
                    </div>
                    {!!(config.iFrameConfig?.enableOMFEBetslipBubble) && (
                        <BreakpointWrapper breakpoints={[Breakpoints.Small]} action="show">
                            <BrowserOnly>
                                <BetslipBubble />
                            </BrowserOnly>
                        </BreakpointWrapper>
                    )}
                    {
                        config.customConfig?.casinoFooterUrl && config.customConfig?.version !== CasinoFooterVersion.V3 ? (
                            <CasinoFooter className="Footer" />
                        ) : (
                            <BrowserOnly>
                                <FooterTemplate /> 
                            </BrowserOnly>
                        )
                    }
                </section>
                <BrowserOnly>
                    <BreakpointWrapper breakpoints={[Breakpoints.Small]}>
                        <BetslipWrapperMobile />
                    </BreakpointWrapper>
                </BrowserOnly>
                <BreakpointWrapper breakpoints={[Breakpoints.Small]} action="hide">
                    {isBrowser() ? <RightSideBar {...this.props} /> : <RightSideBarSkeleton />}
                </BreakpointWrapper>
            </>
        );
    }
}

const LiveTemplate: React.ComponentType<BaseTemplatePublicProps> = compose(
    withRouting<BaseTemplatePublicProps>(),
)(LiveTemplateComponent);
export default LiveTemplate;
